import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  where,
  orderBy,
  getDocs,
  setDoc,
  Unsubscribe,
  deleteField,
} from 'firebase/firestore';
import { db } from './firebase';
import type { AnnouncementBanner, UserAnnouncementDismissal } from '../types';

const ANNOUNCEMENTS_COLLECTION = 'announcements';
const USER_CONTENT_COLLECTION = 'userContent';
const DISMISSED_ANNOUNCEMENTS_SUBCOLLECTION = 'dismissedAnnouncements';

// ============================================
// ADMIN: CRUD for announcement banners
// ============================================

/** Subscribe to all announcements (admin panel) */
export function subscribeToAnnouncements(
  callback: (announcements: AnnouncementBanner[]) => void,
): Unsubscribe {
  const q = query(
    collection(db, ANNOUNCEMENTS_COLLECTION),
    orderBy('createdAt', 'desc'),
  );
  return onSnapshot(q, (snapshot) => {
    const items = snapshot.docs.map((d) => ({
      ...d.data(),
      id: d.id,
    })) as AnnouncementBanner[];
    callback(items);
  });
}

/** Create a new announcement */
export async function createAnnouncement(
  data: Omit<AnnouncementBanner, 'id' | 'createdAt' | 'updatedAt'>,
): Promise<string> {
  const now = new Date().toISOString();
  // Strip undefined values — Firestore rejects them
  const cleaned: Record<string, any> = { createdAt: now, updatedAt: now };
  for (const [key, value] of Object.entries(data)) {
    if (value !== undefined) cleaned[key] = value;
  }
  const docRef = await addDoc(collection(db, ANNOUNCEMENTS_COLLECTION), cleaned);
  return docRef.id;
}

/** Update an existing announcement */
export async function updateAnnouncement(
  id: string,
  updates: Partial<AnnouncementBanner>,
): Promise<void> {
  const docRef = doc(db, ANNOUNCEMENTS_COLLECTION, id);
  // Replace undefined with deleteField() for Firestore
  const cleaned: Record<string, any> = { updatedAt: new Date().toISOString() };
  for (const [key, value] of Object.entries(updates)) {
    if (key === 'id' || key === 'createdAt') continue;
    cleaned[key] = value === undefined ? deleteField() : value;
  }
  await updateDoc(docRef, cleaned);
}

/** Delete an announcement */
export async function deleteAnnouncement(id: string): Promise<void> {
  await deleteDoc(doc(db, ANNOUNCEMENTS_COLLECTION, id));
}

// ============================================
// CONSUMER: Active announcements + dismissals
// ============================================

/** Subscribe to active announcements (consumer app) */
export function subscribeToActiveAnnouncements(
  callback: (announcements: AnnouncementBanner[]) => void,
): Unsubscribe {
  const q = query(
    collection(db, ANNOUNCEMENTS_COLLECTION),
    where('active', '==', true),
    orderBy('startTime', 'desc'),
  );
  return onSnapshot(q, (snapshot) => {
    const now = new Date().toISOString();
    const items = snapshot.docs
      .map((d) => ({ ...d.data(), id: d.id }) as AnnouncementBanner)
      .filter((a) => {
        if (a.startTime > now) return false;
        if (a.endTime && a.endTime < now) return false;
        return true;
      });
    callback(items);
  });
}

/** Subscribe to a user's dismissed announcement IDs */
export function subscribeToDismissedAnnouncements(
  uid: string,
  callback: (dismissedIds: Set<string>) => void,
): Unsubscribe {
  const colRef = collection(
    db,
    USER_CONTENT_COLLECTION,
    uid,
    DISMISSED_ANNOUNCEMENTS_SUBCOLLECTION,
  );
  return onSnapshot(colRef, (snapshot) => {
    const ids = new Set(snapshot.docs.map((d) => d.id));
    callback(ids);
  });
}

/** Dismiss an announcement for a user */
export async function dismissAnnouncement(
  uid: string,
  announcementId: string,
): Promise<void> {
  const docRef = doc(
    db,
    USER_CONTENT_COLLECTION,
    uid,
    DISMISSED_ANNOUNCEMENTS_SUBCOLLECTION,
    announcementId,
  );
  await setDoc(docRef, {
    announcementId,
    dismissedAt: new Date().toISOString(),
  } satisfies UserAnnouncementDismissal);
}
